<template>
  <transition name="config-panel">
    <div
      v-if="!!configType"
      data-cy="do-config-panel"
      class="relative z-10 flex h-full transform flex-col border shadow-lg transition-all duration-500"
    >
      <div class="flex h-full w-93 flex-col">
        <div class="flex items-center justify-between p-5">
          <component :is="panelTop" />
          <div
            data-cy="config-panel-close"
            class="cursor-pointer"
            @click="deselectStep"
          >
            <icon-base :icon="IconX" />
          </div>
        </div>
        <component :is="panel" :key="activeStep?.id" />
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount } from "vue";
import { useStore } from "vuex";

import {
  PANEL_TYPE_CONDITIONAL,
  PANEL_TYPE_OTHER,
  PANEL_TYPE_OTHER_CUSTOM,
  PANEL_TYPE_SERVICE,
  PANEL_TYPE_TRIGGER,
} from "@/helpers/constants";
import { useDataOrchestration } from "@/hooks/orchestration";

import ApplicationData from "./configPanel/ApplicationData.vue";
import ApplicationDataTop from "./configPanel/ApplicationDataTop.vue";
import Conditional from "./configPanel/Conditional.vue";
import ConditionalTop from "./configPanel/ConditionalTop.vue";
import Service from "./configPanel/Service.vue";
import ServiceTop from "./configPanel/ServiceTop.vue";
import Trigger from "./configPanel/Trigger.vue";
import TriggerTop from "./configPanel/TriggerTop.vue";
import CustomAttributes from "./configPanel/customAttributes/CustomAttributes.vue";
import CustomAttributesTop from "./configPanel/customAttributes/CustomAttributesTop.vue";
import IconX from "@/components/icons/deals/IconX.vue";
import IconBase from "@/components/ui/IconBase.vue";

import type { ActiveConfigPanel, IStep } from "@/models/orchestration";

const { getters } = useStore();
const { deselectStep } = useDataOrchestration();

const configType = computed<ActiveConfigPanel>(
  () => getters["orchestration/activeConfigPanel"]
);

const activeStep = computed<IStep>(() => getters["orchestration/activeStep"]);

const componentsForPanelTop = {
  [PANEL_TYPE_TRIGGER]: TriggerTop,
  [PANEL_TYPE_SERVICE]: ServiceTop,
  [PANEL_TYPE_CONDITIONAL]: ConditionalTop,
  [PANEL_TYPE_OTHER]: ApplicationDataTop,
  [PANEL_TYPE_OTHER_CUSTOM]: CustomAttributesTop,
};

const componentsForPanel = {
  [PANEL_TYPE_TRIGGER]: Trigger,
  [PANEL_TYPE_SERVICE]: Service,
  [PANEL_TYPE_CONDITIONAL]: Conditional,
  [PANEL_TYPE_OTHER]: ApplicationData,
  [PANEL_TYPE_OTHER_CUSTOM]: CustomAttributes,
};

const panelTop = computed(() =>
  configType.value ? componentsForPanelTop[configType.value] : null
);

const panel = computed(() =>
  configType.value ? componentsForPanel[configType.value] : null
);

onBeforeUnmount(() => {
  deselectStep();
});
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.config-panel-enter-active {
  transition: all 0.2s ease-out;
}

.config-panel-leave-active {
  transition: all 0.2s ease-out;
}

.config-panel-enter-from,
.config-panel-leave-to {
  @apply translate-x-full transform;
  opacity: 0;
}
</style>
